"use client"

import { useState, lazy, Suspense } from 'react'
import * as stylex from '@stylexjs/stylex'
import { ImageGalleryItem } from '@/models/gallery'
import { styles } from '@/styles/ImagePage.stylex'

const ImagePopup = lazy(() => import('@/components/popup/ImagePopup'))

interface TextImageToImageGalleryProps {
    title: string
    description: string
    modelKey: string
    modelLabel: string
    modelNote: string
    allImages: string[]
    items: ImageGalleryItem[]
}

export function TextImageToImageGallery({ title, description, modelKey, modelLabel, modelNote, items, allImages }: TextImageToImageGalleryProps) {
    const [showPopup, setShowPopup] = useState(false)
    const [selectedItem, setSelectedItem] = useState<ImageGalleryItem>({ id: '', src: '', className: '', prompt: '', width: 0, height: 0, aspect_ratio: '16:9', referenceImages: [] })

    const openPopup = (item: ImageGalleryItem) => {
        setSelectedItem(item)
        setShowPopup(true)
    }

    return (
        <>
            <div {...stylex.props(styles.gallerySection)}>
                <h3 {...stylex.props(styles.sectionTitle)}>{title}</h3>
                <p {...stylex.props(styles.galleryDescription)} dangerouslySetInnerHTML={{ __html: description }} />

                <div {...stylex.props(styles.textImageList)}>
                    {items.map((item, index) => (
                        <TextImageItem
                            key={index}
                            item={item}
                            modelLabel={modelLabel}
                            onClick={() => openPopup(item)}
                        />
                    ))}
                </div>
            </div>

            <Suspense fallback={null}>
                {showPopup && (
                    <ImagePopup
                        isOpen={showPopup}
                        onClose={() => setShowPopup(false)}
                        id={selectedItem.id}
                        src={selectedItem.src}
                        allImages={allImages}
                        selectedReferenceImages={selectedItem.referenceImages || []}
                        modelKey={modelKey}
                        modelLabel={modelLabel}
                        prompt={selectedItem.prompt}
                        aspect_ratio={selectedItem.aspect_ratio}
                        popupTitle="Image Details"
                        modelNote={modelNote}
                        variants={selectedItem.variants}
                    />
                )}
            </Suspense>
        </>
    )
}

function TextImageItem({ item, modelLabel, onClick }: { item: ImageGalleryItem, modelLabel: string, onClick: () => void }) {
    const [isHovered, setIsHovered] = useState(false)
    const referenceImages = item.referenceImages || []
    const count = referenceImages.length

    const renderReferenceImages = () => {
        if (count === 0) return null

        if (count === 1) {
            return (
                <div {...stylex.props(styles.referenceImages)}>
                    <img
                        src={referenceImages[0]}
                        {...stylex.props(styles.comparisonImage, isHovered && styles.comparisonImageHover)}
                        alt="Reference"
                    />
                    <div {...stylex.props(styles.imagePrompt, isHovered && styles.imagePromptVisible)}>
                        <p {...stylex.props(styles.galleryPromptText)}>{item.prompt}</p>
                    </div>
                </div>
            )
        }

        if (count === 2) {
            return (
                <div {...stylex.props(styles.referenceImages, styles.twoImages)}>
                    <img
                        src={referenceImages[0]}
                        {...stylex.props(styles.comparisonImage, styles.twoImagesTopLeft, isHovered && styles.multiImageImgHover)}
                        alt="Ref 1"
                    />
                    <img
                        src={referenceImages[1]}
                        {...stylex.props(styles.comparisonImage, styles.twoImagesBottomRight, isHovered && styles.multiImageImgHover)}
                        alt="Ref 2"
                    />
                    <div {...stylex.props(styles.galleryPrompt, isHovered && styles.galleryPromptVisible)}>
                        <p {...stylex.props(styles.galleryPromptText)}>{item.prompt}</p>
                    </div>
                </div>
            )
        }

        if (count >= 3) {
            const imagesToShow = referenceImages.slice(0, 4)
            return (
                <div {...stylex.props(styles.referenceImages, styles.fourImages)}>
                    <img
                        src={imagesToShow[0]}
                        {...stylex.props(styles.comparisonImage, styles.fourImagesItem, styles.fourImagesTopLeft, isHovered && styles.multiImageImgHover)}
                        alt="Ref 1"
                    />
                    <img
                        src={imagesToShow[1]}
                        {...stylex.props(styles.comparisonImage, styles.fourImagesItem, styles.fourImagesTopRight, isHovered && styles.multiImageImgHover)}
                        alt="Ref 2"
                    />
                    <img
                        src={imagesToShow[2]}
                        {...stylex.props(styles.comparisonImage, styles.fourImagesItem, styles.fourImagesBottomLeft, isHovered && styles.multiImageImgHover)}
                        alt="Ref 3"
                    />
                    {imagesToShow[3] && (
                        <img
                            src={imagesToShow[3]}
                            {...stylex.props(styles.comparisonImage, styles.fourImagesItem, styles.fourImagesBottomRight, isHovered && styles.multiImageImgHover)}
                            alt="Ref 4"
                        />
                    )}
                    <div {...stylex.props(styles.galleryPrompt, isHovered && styles.galleryPromptVisible)}>
                        <p {...stylex.props(styles.galleryPromptText)}>{item.prompt}</p>
                    </div>
                </div>
            )
        }
        return null;
    }

    return (
        <div
            {...stylex.props(styles.textImageItem)}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div {...stylex.props(styles.imagePair)}>
                {renderReferenceImages()}
                <div {...stylex.props(styles.arrowSection)}>
                    <div {...stylex.props(styles.arrow, isHovered && styles.arrowHover)}>→</div>
                </div>
                <div {...stylex.props(styles.generatedImage)}>
                    <img
                        src={item.src}
                        {...stylex.props(styles.comparisonImage, isHovered && styles.comparisonImageHover)}
                        alt={`${modelLabel} generated`}
                    />
                    {/* Prompt overlay removed from here */}
                </div>
            </div>
            <div {...stylex.props(styles.clickIndicator, isHovered && styles.clickIndicatorVisible)}>
                Click me
            </div>
        </div>
    )
}